import {
  PAIR_KIND,
  getPairBrand,
  getPairKind,
  getPairPrice,
  isPairLocalStock,
} from "../list/tirePairModel";

export const DEFAULT_PAIR_FILTERS = {
  brands: [],
  localStockOnly: false,
  type: "all",
  minPrice: null,
  maxPrice: null,
  sort: "recommended",
};

const FILTER_QUERY_KEYS = ["brands", "stock", "type", "min", "max", "sort"];

function uniqueLower(values) {
  const seen = new Set();
  const result = [];

  values.forEach((value) => {
    const key = String(value || "").trim().toLowerCase();
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    result.push(key);
  });

  return result;
}

function toPrice(value) {
  if (value == null || value === "") {
    return null;
  }

  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) {
    return null;
  }

  return Math.round(number);
}

function normalizePairFilters(filters) {
  const source = filters && typeof filters === "object" ? filters : {};
  const type = source.type === "set" || source.type === "pair" ? source.type : "all";
  const sort =
    source.sort === "price_asc" || source.sort === "price_desc" ? source.sort : "recommended";

  let minPrice = toPrice(source.minPrice);
  let maxPrice = toPrice(source.maxPrice);
  if (minPrice != null && maxPrice != null && minPrice > maxPrice) {
    const swap = minPrice;
    minPrice = maxPrice;
    maxPrice = swap;
  }

  return {
    brands: uniqueLower(Array.isArray(source.brands) ? source.brands : []),
    localStockOnly: !!source.localStockOnly,
    type,
    minPrice,
    maxPrice,
    sort,
  };
}

export function parsePairFiltersFromSearch(search) {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  const minRaw = params.get("min");
  const maxRaw = params.get("max");

  return normalizePairFilters({
    brands: (params.get("brands") || "").split(","),
    localStockOnly: params.get("stock") === "local",
    type: params.get("type"),
    minPrice: minRaw,
    maxPrice: maxRaw,
    sort: params.get("sort"),
  });
}

export function getSearchForPairFilters(search, filters) {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  const normalized = normalizePairFilters(filters);

  FILTER_QUERY_KEYS.forEach((key) => params.delete(key));

  if (normalized.brands.length > 0) {
    params.set("brands", normalized.brands.join(","));
  }
  if (normalized.localStockOnly) {
    params.set("stock", "local");
  }
  if (normalized.type !== "all") {
    params.set("type", normalized.type);
  }
  if (normalized.minPrice != null) {
    params.set("min", String(normalized.minPrice));
  }
  if (normalized.maxPrice != null) {
    params.set("max", String(normalized.maxPrice));
  }
  if (normalized.sort !== "recommended") {
    params.set("sort", normalized.sort);
  }

  const next = params.toString();
  return next ? `?${next}` : "";
}

export function applyPairFilters(pairs, filters) {
  const list = Array.isArray(pairs) ? pairs.slice() : [];
  const normalized = normalizePairFilters(filters);
  const selectedBrands = new Set(normalized.brands);

  const filtered = list.filter((pair) => {
    const kind = getPairKind(pair);
    if (normalized.type === "set" && kind !== PAIR_KIND.SET) {
      return false;
    }
    if (normalized.type === "pair" && kind !== PAIR_KIND.PAIR) {
      return false;
    }
    if (normalized.localStockOnly && !isPairLocalStock(pair)) {
      return false;
    }
    if (selectedBrands.size > 0) {
      const brand = String(getPairBrand(pair) || "").trim().toLowerCase();
      if (!selectedBrands.has(brand)) {
        return false;
      }
    }

    const price = getPairPrice(pair);
    if (normalized.minPrice != null && price < normalized.minPrice) {
      return false;
    }
    if (normalized.maxPrice != null && price > normalized.maxPrice) {
      return false;
    }

    return true;
  });

  if (normalized.sort === "recommended") {
    return filtered;
  }

  const sorted = filtered.slice();
  sorted.sort((left, right) => {
    const delta = getPairPrice(left) - getPairPrice(right);
    if (delta === 0) {
      return 0;
    }
    return normalized.sort === "price_desc" ? -delta : delta;
  });

  return sorted;
}

export function getPairFacets(pairs) {
  const list = Array.isArray(pairs) ? pairs : [];
  const brandCounts = new Map();
  let rawMin = null;
  let rawMax = null;
  let setCount = 0;
  let pairCount = 0;
  let localStockCount = 0;

  list.forEach((pair) => {
    const brand = String(getPairBrand(pair) || "").trim();
    if (brand) {
      const key = brand.toLowerCase();
      const prev = brandCounts.get(key);
      brandCounts.set(key, {
        brand: prev ? prev.brand : brand,
        count: prev ? prev.count + 1 : 1,
      });
    }

    const price = getPairPrice(pair);
    if (Number.isFinite(price)) {
      rawMin = rawMin == null ? price : Math.min(rawMin, price);
      rawMax = rawMax == null ? price : Math.max(rawMax, price);
    }

    if (getPairKind(pair) === PAIR_KIND.SET) {
      setCount += 1;
    } else {
      pairCount += 1;
    }

    if (isPairLocalStock(pair)) {
      localStockCount += 1;
    }
  });

  const brands = Array.from(brandCounts.values()).sort((left, right) => {
    if (right.count !== left.count) {
      return right.count - left.count;
    }
    return left.brand.localeCompare(right.brand, "he");
  });

  let priceMin = null;
  let priceMax = null;
  if (rawMin != null) {
    if (rawMin === rawMax) {
      priceMin = Math.round(rawMin);
      priceMax = priceMin;
    } else {
      priceMin = Math.floor(rawMin);
      priceMax = Math.ceil(rawMax);
    }
  }

  return {
    brands,
    priceMin,
    priceMax,
    setCount,
    pairCount,
    localStockCount,
    totalCount: list.length,
  };
}

export function getVisiblePairFilterSections(facets) {
  const source = facets && typeof facets === "object" ? facets : {};
  const brands = Array.isArray(source.brands) ? source.brands : [];
  const sections = {
    type: source.setCount > 0 && source.pairCount > 0,
    availability:
      source.localStockCount > 0 && source.localStockCount < source.totalCount,
    price:
      source.priceMin != null &&
      source.priceMax != null &&
      source.priceMin !== source.priceMax,
    brands: brands.length > 1,
  };

  sections.any = sections.type || sections.availability || sections.price || sections.brands;
  return sections;
}

export function countActivePairFilters(filters) {
  const normalized = normalizePairFilters(filters);
  let count = normalized.brands.length;

  if (normalized.localStockOnly) {
    count += 1;
  }
  if (normalized.type === "set" || normalized.type === "pair") {
    count += 1;
  }
  if (normalized.minPrice != null || normalized.maxPrice != null) {
    count += 1;
  }

  return count;
}
